/**
 * Settlement Base Helpers
 * 정산 관련 공통 헬퍼 함수 및 타입
 */
import { formatCurrency } from '@/shared/utils/formatter';
import { isExcelFile } from '@/shared/utils/validation';

// 공통 유틸리티 재내보내기
export { formatCurrency };
export { isExcelFile as isValidExcelFile };

/**
 * 테이블 컬럼 정의 인터페이스
 */
export interface TableColumn {
    index: number;
    header: string;
    width: string;
    minWidth: string;
    tooltip?: string;
}

/**
 * 필터 유효성 검사 결과 타입
 */
export interface FilterValidation {
    isValid: boolean;
    message?: string;
}

/**
 * 권한별 컬럼 표시 여부 확인
 * @param columnIndex - 컬럼 인덱스
 * @param visibleColumns - 표시할 컬럼 인덱스 배열
 * @param isRiderUser - 라이더 사용자 여부
 */
export const shouldShowColumn = (
    columnIndex: number,
    visibleColumns: readonly number[],
    isRiderUser: boolean
): boolean => {
    if (!isRiderUser) return true;
    return visibleColumns.includes(columnIndex);
};

/**
 * 컬럼 정렬 스타일 반환
 * @param columnIndex - 컬럼 인덱스
 * @param centerColumns - 중앙 정렬 컬럼 인덱스 배열
 * @param currencyColumns - 금액 컬럼 인덱스 배열 (우측 정렬)
 */
export const getColumnAlignment = (
    columnIndex: number,
    centerColumns: readonly number[],
    currencyColumns: readonly number[]
): React.CSSProperties => {
    if (centerColumns.includes(columnIndex)) {
        return { textAlign: 'center' };
    }
    if (currencyColumns.includes(columnIndex)) {
        return { textAlign: 'right' };
    }
    return {};
};

/**
 * 날짜 필터 기본 유효성 검사
 * @param startDate - 시작일
 * @param endDate - 종료일
 */
export const validateDateRange = (
    startDate: string,
    endDate: string
): FilterValidation => {
    if (!startDate || !endDate) {
        return { isValid: false, message: '조회일자를 선택해주세요.' };
    }
    if (startDate > endDate) {
        return { isValid: false, message: '시작일이 종료일보다 늦을 수 없습니다.' };
    }
    return { isValid: true };
};

/**
 * 초기 날짜 범위 반환 (종료일 기준 N일 전)
 * @param endDate - 종료일
 * @param daysBack - 시작일까지의 일수 (기본 6일 = 7일 범위)
 */
export const calculateDateRange = (
    endDate: string,
    daysBack = 6
): { startDate: string; endDate: string } => {
    const end = new Date(endDate);
    const start = new Date(end);
    start.setDate(start.getDate() - daysBack);

    return {
        startDate: start.toISOString().split('T')[0],
        endDate
    };
};
